import { useMemo, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import SEO from "@/components/SEO";
import { useAuth } from "@/hooks/useAuth";
import Footer from "@/components/Footer";
import CardFlipDemo from "@/components/CardFlipDemo";
import TherapistCard from "@/components/TherapistCard";
import { Sparkles, MessageCircle, Sun } from "lucide-react";

const BAND_VIDEO_2 = "/masshappy-band-2.mp4";
const BAND_POSTER_2 = "/masshappy-band-2-poster.jpg";

const APP_URL = "https://app.masshappy.com";

const ALL_CITIES = "__all__";

const scrollTo = (id: string) => {
  document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
};

const Home = () => {
  const { t, i18n } = useTranslation();
  const locale = (i18n.resolvedLanguage || i18n.language || "es").split("-")[0];
  const [selectedCity, setSelectedCity] = useState<string>(ALL_CITIES);
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Alta implicita de cliente: mostrar interes por un terapeuta crea su ficha.
  const handleInterest = async () => {
    if (!user) {
      navigate("/acceso", { state: { from: `${location.pathname}#terapeutas` } });
      return;
    }
    const { error } = await supabase.rpc("ensure_client_profile");
    if (error) console.warn("No se pudo crear la ficha de cliente:", error.message);
    toast(t("home.therapists.joinToast"));
  };

  const appBenefits = [
    { icon: Sun, title: t("home.app.b1title"), text: t("home.app.b1text") },
    { icon: Sparkles, title: t("home.app.b2title"), text: t("home.app.b2text") },
    { icon: MessageCircle, title: t("home.app.b3title"), text: t("home.app.b3text") },
  ];

  const { data, isLoading, error } = useQuery({
    queryKey: ["public-therapists"],
    queryFn: async () => {
      const { data, error } = await supabase.rpc("get_public_therapists");
      if (error) throw error;
      return data ?? [];
    },
  });

  const therapists = data ?? [];

  const cities = useMemo(
    () =>
      Array.from(
        new Set(therapists.map((t) => t.ciudad).filter(Boolean) as string[])
      ).sort((a, b) => a.localeCompare(b, locale)),
    [therapists, locale]
  );

  const filtered = useMemo(
    () =>
      selectedCity === ALL_CITIES
        ? therapists
        : therapists.filter((t) => t.ciudad === selectedCity),
    [therapists, selectedCity]
  );

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title={t("home.seo.title")}
        description={t("home.seo.description")}
        url="https://masshappy.com/"
      />

      <main>
        {/* 1. HERO */}
        <section className="container mx-auto max-w-3xl px-4 pt-20 pb-16 text-center">
          <h1 className="font-serif text-4xl md:text-6xl font-semibold leading-tight text-foreground">
            {t("home.hero.title1")}
            <br />
            <span className="text-primary">{t("home.hero.title2")}</span>
          </h1>
          <p className="mt-6 text-base md:text-lg text-muted-foreground leading-relaxed">
            {t("home.hero.lead")}
          </p>
          <div className="mt-10">
            <Button
              size="lg"
              onClick={() => scrollTo("app")}
              className="px-10 py-6 text-base shadow-medium"
            >
              {t("home.hero.cta")}
            </Button>
            <div className="mt-5">
              <button
                type="button"
                onClick={() => scrollTo("terapeutas")}
                className="text-sm text-muted-foreground underline underline-offset-4 hover:text-primary transition-colors"
              >
                {t("home.hero.secondary")}
              </button>
            </div>
          </div>
        </section>

        {/* 2. FRANJA A SANGRE CON VÍDEO */}
        <section className="relative isolate overflow-hidden h-[320px] md:h-[460px] flex items-center justify-center px-6">
          <video
            aria-hidden="true"
            className="absolute inset-0 h-full w-full object-cover motion-reduce:hidden"
            src={BAND_VIDEO_2}
            poster={BAND_POSTER_2}
            autoPlay
            loop
            muted
            playsInline
            preload="metadata"
          />
          <img
            aria-hidden="true"
            alt=""
            src={BAND_POSTER_2}
            className="absolute inset-0 h-full w-full object-cover hidden motion-reduce:block"
          />
          <div
            aria-hidden="true"
            className="absolute inset-0 opacity-70 mix-blend-multiply"
            style={{ background: "var(--gradient-warm)" }}
          />
          <div aria-hidden="true" className="absolute inset-0 bg-foreground/25" />
          <p className="relative font-serif text-3xl md:text-5xl text-center text-primary-foreground animate-in fade-in duration-1000">
            {t("home.band")}
          </p>
        </section>

        {/* 3. LA APP */}
        <section
          id="app"
          className="bg-muted/40 border-y border-border py-16 md:py-20 scroll-mt-24"
        >
          <div className="container mx-auto max-w-3xl px-4">
            <h2 className="font-serif text-2xl md:text-4xl text-center text-foreground">
              {t("home.app.title")}
            </h2>
            <p className="mt-4 mb-10 text-center text-sm md:text-base text-muted-foreground leading-relaxed">
              {t("home.app.lead")}
            </p>

            <CardFlipDemo />

            <div className="mt-12 grid grid-cols-1 sm:grid-cols-3 gap-6">
              {appBenefits.map((b) => (
                <div key={b.title} className="text-center sm:text-left">
                  <b.icon
                    aria-hidden="true"
                    className="mx-auto sm:mx-0 h-5 w-5 text-primary"
                  />
                  <h3 className="mt-3 font-serif text-lg text-foreground">
                    {b.title}
                  </h3>
                  <p className="mt-1 text-sm text-muted-foreground leading-relaxed">
                    {b.text}
                  </p>
                </div>
              ))}
            </div>

            <div className="mt-12 text-center">
              <Button asChild size="lg" className="shadow-medium">
                <a href={APP_URL} target="_blank" rel="noopener noreferrer">
                  {t("home.app.cta")}
                </a>
              </Button>
            </div>

            <div className="mt-8 rounded-[--radius] border border-border bg-card p-5 md:p-6 shadow-soft">
              <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground text-center">
                {t("home.app.pwaTitle")}
              </p>
              <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <h4 className="text-sm font-medium text-foreground">
                    {t("home.app.iphoneTitle")}
                  </h4>
                  <p className="mt-1 text-sm text-muted-foreground leading-relaxed">
                    {t("home.app.iphoneText")}
                  </p>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-foreground">
                    {t("home.app.androidTitle")}
                  </h4>
                  <p className="mt-1 text-sm text-muted-foreground leading-relaxed">
                    {t("home.app.androidText")}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 4. TERAPEUTAS */}
        <section
          id="terapeutas"
          className="container mx-auto max-w-6xl px-4 py-16 scroll-mt-24"
        >
          <h2 className="font-serif text-2xl md:text-4xl text-center text-foreground">
            {t("home.therapists.title1")}
            <br className="hidden sm:block" />{" "}
            <span className="text-primary">{t("home.therapists.title2")}</span>
          </h2>
          <p className="mt-4 text-center text-sm md:text-base text-muted-foreground leading-relaxed max-w-2xl mx-auto">
            {t("home.therapists.lead")}
          </p>

          {cities.length > 0 && (
            <div className="mt-8 flex flex-wrap justify-center gap-2">
              {[ALL_CITIES, ...cities].map((c) => {
                const active = selectedCity === c;
                return (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setSelectedCity(c)}
                    className={`rounded-full px-4 py-1.5 text-xs transition-colors ${
                      active
                        ? "bg-primary text-primary-foreground"
                        : "bg-muted text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    {c === ALL_CITIES ? t("home.therapists.all") : c}
                  </button>
                );
              })}
            </div>
          )}


          <div className="mt-6">
            {isLoading && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {[0, 1, 2].map((i) => (
                  <div
                    key={i}
                    className="overflow-hidden rounded-xl border border-border bg-card shadow-soft animate-pulse"
                  >
                    <div className="aspect-[4/3] w-full bg-muted" />
                    <div className="space-y-3 p-4">
                      <div className="h-4 w-2/3 rounded bg-muted" />
                      <div className="h-3 w-1/3 rounded bg-muted" />
                      <div className="h-3 w-1/2 rounded bg-muted" />
                    </div>
                  </div>
                ))}
              </div>
            )}

            {!isLoading && error && (
              <p className="text-center text-sm text-muted-foreground">
                {t("home.therapists.error")}
              </p>
            )}

            {!isLoading && !error && filtered.length === 0 && (
              <p className="text-center text-sm text-muted-foreground">
                {t("home.therapists.empty")}
              </p>
            )}

            {!isLoading && !error && filtered.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filtered.map((th) => (
                  <TherapistCard key={th.id} therapist={th} />
                ))}
              </div>
            )}
          </div>

          <div className="mt-10 text-center">
            <button
              type="button"
              className="text-sm text-muted-foreground underline underline-offset-4 hover:text-primary transition-colors"
              onClick={handleInterest}
            >
              {t("home.therapists.join")}
            </button>
          </div>
        </section>

        {/* 5. LAS TRES EXPERIENCIAS */}
        <section className="container mx-auto max-w-6xl px-4 py-16 md:py-20">
          <h2 className="font-serif text-2xl md:text-4xl text-center text-foreground">
            {t("home.experiences.title")}
          </h2>
          <p className="mt-4 text-center text-sm md:text-base text-muted-foreground leading-relaxed max-w-2xl mx-auto">
            {t("home.experiences.lead")}
          </p>

          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card className="border-border shadow-soft flex flex-col">
              <CardContent className="p-6 flex flex-col h-full">
                <span className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
                  {t("home.experiences.c1tag")}
                </span>
                <h3 className="mt-3 font-serif text-xl md:text-2xl text-primary">
                  {t("home.experiences.c1title")}
                </h3>
                <p className="mt-3 text-sm text-muted-foreground leading-relaxed flex-1">
                  {t("home.experiences.c1text")}
                </p>
                <Button className="mt-6 w-full" onClick={() => scrollTo("app")}>
                  {t("home.experiences.c1cta")}
                </Button>
              </CardContent>
            </Card>

            <Card className="border-border shadow-soft flex flex-col">
              <CardContent className="p-6 flex flex-col h-full">
                <span className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
                  {t("home.experiences.c2tag")}
                </span>
                <h3 className="mt-3 font-serif text-xl md:text-2xl text-primary">
                  {t("home.experiences.c2title")}
                </h3>
                <p className="mt-3 text-sm text-muted-foreground leading-relaxed flex-1">
                  {t("home.experiences.c2text")}
                </p>
                <Button
                  variant="outline"
                  className="mt-6 w-full"
                  onClick={() => scrollTo("terapeutas")}
                >
                  {t("home.experiences.c2cta")}
                </Button>
              </CardContent>
            </Card>

            <Card className="border-border shadow-soft flex flex-col">
              <CardContent className="p-6 flex flex-col h-full">
                <span className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
                  {t("home.experiences.c3tag")}
                </span>
                <h3 className="mt-3 font-serif text-xl md:text-2xl text-primary">
                  {t("home.experiences.c3title")}
                </h3>
                <p className="mt-3 text-sm text-muted-foreground leading-relaxed flex-1">
                  {t("home.experiences.c3text")}
                </p>
                <Button variant="outline" className="mt-6 w-full" asChild>
                  <Link to="/retiro">{t("home.experiences.c3cta")}</Link>
                </Button>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* 6. LA RESIDENCIA */}
        <section className="container mx-auto max-w-3xl px-4 py-16">
          <Card
            className="shadow-medium"
            style={{ borderColor: "hsl(var(--wellness-gold) / 0.5)" }}
          >
            <CardContent className="p-8 md:p-10 text-center">
              <span
                className="text-[11px] uppercase tracking-[0.18em]"
                style={{ color: "hsl(var(--wellness-gold))" }}
              >
                {t("home.residency.tag")}
              </span>
              <h2 className="mt-3 font-serif text-2xl md:text-3xl text-primary">
                {t("home.residency.title")}
              </h2>
              <p className="mt-4 text-sm md:text-base text-muted-foreground leading-relaxed">
                {t("home.residency.text")}
              </p>
              <div className="mt-8">
                <Button asChild size="lg">
                  <Link to="/retiro">{t("home.residency.cta")}</Link>
                </Button>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* 7. HONESTIDAD */}
        <section className="bg-muted py-16">
          <div className="container mx-auto max-w-2xl px-4 text-center">
            <p className="text-sm md:text-base text-muted-foreground leading-relaxed">
              {t("home.honesty")}
            </p>
          </div>
        </section>

        {/* 8. CIERRE */}
        <section className="container mx-auto max-w-2xl px-4 py-20 text-center">
          <h2 className="font-serif text-2xl md:text-3xl text-foreground">
            {t("home.closing.title")}
          </h2>
          <p className="mt-4 text-sm md:text-base text-muted-foreground leading-relaxed">
            {t("home.closing.text")}
          </p>
          <div className="mt-8">
            <Button asChild size="lg" className="px-10 py-6 text-base shadow-medium">
              <a href={APP_URL} target="_blank" rel="noopener noreferrer">
                {t("home.closing.cta")}
              </a>
            </Button>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default Home;
